import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Camera, Music, Youtube, Loader2, Upload, FileImage, X, CheckCircle2, FileText, Type, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPORT_TABS = [
  { id: 'image', label: 'Photo', icon: Camera, desc: 'Snap notes or whiteboard' },
  { id: 'audio', label: 'Audio', icon: Music, desc: 'Upload a recording' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, desc: 'Import from video' },
  { id: 'slides', label: 'Slides', icon: FileText, desc: 'Upload PPTX or PDF' },
  { id: 'text', label: 'Text', icon: Type, desc: 'Paste from any source' },
];

const IMPORTING_LABELS = {
  youtube: 'Downloading & Transcribing...',
  audio: 'Transcribing...',
  slides: 'Extracting Slides...',
  text: 'Creating Lecture...',
};

const getImportingLabel = (batchMode, tab) => {
  if (batchMode) return 'Processing Batch...';
  return IMPORTING_LABELS[tab] || 'Extracting Text...';
};

export const ImportModal = ({ open, onOpenChange, subjects, onSuccess }) => {
  const [tab, setTab] = useState('image');
  const [title, setTitle] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [batchMode, setBatchMode] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setTitle('');
    setSubjectId('');
    setYoutubeUrl('');
    setFile(null);
    setFiles([]);
    setPreview(null);
    setResult(null);
    setPasteText('');
    setBatchMode(false);
    setBatchResults(null);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    if (batchMode && selectedFiles.length > 1) {
      setFiles(selectedFiles);
      setFile(null);
      setPreview(null);
      if (!title) setTitle(`Batch Import (${selectedFiles.length} files)`);
    } else {
      const f = selectedFiles[0];
      setFile(f);
      setFiles([]);
      if (tab === 'image' && f.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setPreview(ev.target.result);
        reader.readAsDataURL(f);
      } else {
        setPreview(null);
      }
      if (!title) {
        setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
      }
    }
  };

  const handleImport = async () => {
    if (!subjectId) return toast.error('Select a subject');
    if (tab !== 'text' && !batchMode && !title.trim()) return toast.error('Enter a title');

    setImporting(true);
    setResult(null);
    setBatchResults(null);

    try {
      let response;

      // Batch mode
      if (batchMode && files.length > 0) {
        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        fd.append('subject_id', subjectId);
        const batchType = tab === 'image' ? 'images' : tab === 'audio' ? 'audio' : 'slides';
        response = await axios.post(`${API}/imports/batch/${batchType}`, fd, { timeout: 300000 });
        setBatchResults(response.data);
        toast.success(`Batch complete: ${response.data.succeeded}/${response.data.total} imported`);
        if (onSuccess) onSuccess(response.data);
        return;
      }

      if (tab === 'text') {
        if (!pasteText.trim() || pasteText.trim().length < 20) return toast.error('Paste at least a paragraph of text');
        if (!title.trim()) return toast.error('Enter a title');
        response = await axios.post(`${API}/imports/text`, {
          text: pasteText.trim(),
          subject_id: subjectId,
          title: title.trim(),
        }, { timeout: 60000 });
      } else if (tab === 'image') {
        if (!file) return toast.error('Select an image');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('subject_id', subjectId);
        fd.append('title', title.trim());
        response = await axios.post(`${API}/imports/image`, fd, { timeout: 60000 });
      } else if (tab === 'audio') {
        if (!file) return toast.error('Select an audio file');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('subject_id', subjectId);
        fd.append('title', title.trim());
        response = await axios.post(`${API}/imports/audio`, fd, { timeout: 120000 });
      } else if (tab === 'youtube') {
        if (!youtubeUrl.trim()) return toast.error('Enter a YouTube URL');
        response = await axios.post(`${API}/imports/youtube`, {
          url: youtubeUrl.trim(),
          subject_id: subjectId,
          title: title.trim(),
        }, { timeout: 180000 });
      } else if (tab === 'slides') {
        if (!file) return toast.error('Select a slide deck');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('subject_id', subjectId);
        fd.append('title', title.trim());
        response = await axios.post(`${API}/imports/slides`, fd, { timeout: 90000 });
      }

      const data = response.data;
      setResult(data);
      toast.success(`Imported successfully! Lecture created.`);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Import failed';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const activeTab = IMPORT_TABS.find((t) => t.id === tab);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-lg bg-[var(--isp-surface)] border-[var(--isp-glass-border)]" data-testid="import-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--isp-heading)] font-heading">Import Lecture</DialogTitle>
        </DialogHeader>

        {/* Tab selector */}
        <div className="flex gap-1 p-1 rounded-xl bg-[var(--isp-glass)]" data-testid="import-tabs">
          {IMPORT_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setFile(null); setFiles([]); setPreview(null); setResult(null); setBatchMode(false); setBatchResults(null); }}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 px-1.5 rounded-lg text-xs font-medium transition-all ${
                tab === t.id
                  ? 'bg-violet-600/20 text-violet-400 shadow-sm'
                  : 'text-[var(--isp-muted)] hover:text-[var(--isp-body)]'
              }`}
              data-testid={`import-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Batch results */}
        {batchResults ? (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="batch-results">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            <p className="text-sm text-[var(--isp-heading)] font-medium">
              Batch Import: {batchResults.succeeded}/{batchResults.total} succeeded
            </p>
            <div className="w-full max-h-40 overflow-y-auto space-y-1">
              {batchResults.results?.map((r, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${r.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
                  {r.success ? <CheckCircle2 className="w-3 h-3 flex-shrink-0" /> : <AlertCircle className="w-3 h-3 flex-shrink-0" />}
                  <span className="truncate">{r.filename}</span>
                  {!r.success && <span className="ml-auto text-[10px] opacity-70">{r.error}</span>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="batch-import-another">Import More</Button>
              <Button size="sm" onClick={() => onOpenChange(false)} data-testid="batch-done">Done</Button>
            </div>
          </div>
        ) : result ? (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="import-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400" />
            <p className="text-sm text-[var(--isp-body)] text-center">
              Lecture <strong className="text-[var(--isp-heading)]">&ldquo;{result.title}&rdquo;</strong> created!
            </p>
            {result.transcript_length && (
              <p className="text-xs text-[var(--isp-muted)]">
                {result.transcript_length.toLocaleString()} characters transcribed
                {result.duration_estimate && ` (${result.duration_estimate})`}
                {result.slides_count && ` from ${result.slides_count} slides`}
              </p>
            )}
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="import-another-btn">
                Import Another
              </Button>
              <Button size="sm" onClick={() => onOpenChange(false)} data-testid="import-done-btn">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Subject selector */}
            <div>
              <Label className="text-xs text-[var(--isp-muted)]">Subject</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]" data-testid="import-subject-select">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label className="text-xs text-[var(--isp-muted)]">Lecture Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Chapter 5 - Cell Biology"
                className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
                data-testid="import-title-input"
              />
            </div>

            {/* Tab-specific content */}
            {tab === 'image' && (
              <div className="space-y-3">
                <p className="text-xs text-[var(--isp-muted)]">
                  Upload a photo of handwritten notes, whiteboard, or printed text. AI vision reads handwriting too.
                </p>
                <div
                  onClick={() => fileRef.current?.click()}
                  className="relative cursor-pointer rounded-xl border-2 border-dashed border-[var(--isp-glass-border)] hover:border-violet-500/40 transition-colors p-6 text-center"
                  data-testid="import-image-dropzone"
                >
                  {batchMode && files.length > 0 ? (
                    <div className="flex items-center gap-3 justify-center">
                      <FileImage className="w-8 h-8 text-violet-400" />
                      <div className="text-left">
                        <p className="text-sm text-[var(--isp-heading)]">{files.length} images selected</p>
                        <p className="text-xs text-[var(--isp-muted)]">{files.map(f => f.name).join(', ').slice(0, 80)}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setFiles([]); }} className="p-1 rounded-full bg-black/50 text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : preview ? (
                    <div className="relative">
                      <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg object-contain" />
                      <button onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }} className="absolute top-1 right-1 p-1 rounded-full bg-black/50 text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <FileImage className="w-10 h-10 mx-auto text-[var(--isp-muted)] mb-2" />
                      <p className="text-sm text-[var(--isp-body)]">Click to upload image</p>
                      <p className="text-xs text-[var(--isp-muted)] mt-1">JPEG, PNG, WebP — max 10MB</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  className="hidden"
                  onChange={handleFileSelect}
                  multiple={batchMode}
                />
              </div>
            )}

            {tab === 'audio' && (
              <div className="space-y-3">
                <p className="text-xs text-[var(--isp-muted)]">
                  Upload a recorded lecture audio file for AI transcription.
                </p>
                <div
                  onClick={() => fileRef.current?.click()}
                  className="relative cursor-pointer rounded-xl border-2 border-dashed border-[var(--isp-glass-border)] hover:border-violet-500/40 transition-colors p-6 text-center"
                  data-testid="import-audio-dropzone"
                >
                  {batchMode && files.length > 0 ? (
                    <div className="flex items-center gap-3 justify-center">
                      <Music className="w-8 h-8 text-violet-400" />
                      <div className="text-left">
                        <p className="text-sm text-[var(--isp-heading)]">{files.length} audio files selected</p>
                        <p className="text-xs text-[var(--isp-muted)]">{files.map(f => f.name).join(', ').slice(0, 80)}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setFiles([]); }} className="p-1 rounded-full bg-black/50 text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : file ? (
                    <div className="flex items-center gap-3 justify-center">
                      <Music className="w-8 h-8 text-violet-400" />
                      <div className="text-left">
                        <p className="text-sm text-[var(--isp-heading)] truncate max-w-[250px]">{file.name}</p>
                        <p className="text-xs text-[var(--isp-muted)]">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="p-1 rounded-full bg-black/50 text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 mx-auto text-[var(--isp-muted)] mb-2" />
                      <p className="text-sm text-[var(--isp-body)]">Click to upload audio</p>
                      <p className="text-xs text-[var(--isp-muted)] mt-1">MP3, WAV, M4A, OGG, WEBM — max 100MB</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="audio/mpeg,audio/wav,audio/mp3,audio/m4a,audio/ogg,audio/webm,audio/flac,.mp3,.wav,.m4a,.ogg,.webm,.flac"
                  className="hidden"
                  onChange={handleFileSelect}
                  multiple={batchMode}
                />
              </div>
            )}

            {tab === 'youtube' && (
              <div className="space-y-3">
                <p className="text-xs text-[var(--isp-muted)]">
                  Paste a YouTube lecture URL to extract and transcribe the audio.
                </p>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" />
                  <Input
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    className="pl-10 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)]"
                    data-testid="import-youtube-url"
                  />
                </div>
                <p className="text-xs text-[var(--isp-muted)] opacity-70">
                  Max 2 hours. Audio is extracted and transcribed with AI.
                </p>
              </div>
            )}

            {tab === 'slides' && (
              <div className="space-y-3">
                <p className="text-xs text-[var(--isp-muted)]">
                  Upload lecture slides (PPTX or PDF) to extract text and generate study materials.
                </p>
                <div
                  onClick={() => fileRef.current?.click()}
                  className="relative cursor-pointer rounded-xl border-2 border-dashed border-[var(--isp-glass-border)] hover:border-violet-500/40 transition-colors p-6 text-center"
                  data-testid="import-slides-dropzone"
                >
                  {file ? (
                    <div className="flex items-center gap-3 justify-center">
                      <FileText className="w-8 h-8 text-indigo-400" />
                      <div className="text-left">
                        <p className="text-sm text-[var(--isp-heading)] truncate max-w-[250px]">{batchMode && files.length > 0 ? `${files.length} files selected` : file.name}</p>
                        <p className="text-xs text-[var(--isp-muted)]">{batchMode && files.length > 0 ? files.map(f => f.name).join(', ').slice(0, 60) : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setFile(null); setFiles([]); }} className="p-1 rounded-full bg-black/50 text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <FileText className="w-10 h-10 mx-auto text-[var(--isp-muted)] mb-2" />
                      <p className="text-sm text-[var(--isp-body)]">Click to upload slides</p>
                      <p className="text-xs text-[var(--isp-muted)] mt-1">PPTX or PDF — max 50MB</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pptx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  className="hidden"
                  onChange={handleFileSelect}
                  multiple={batchMode}
                />
              </div>
            )}

            {tab === 'text' && (
              <div className="space-y-3">
                <p className="text-xs text-[var(--isp-muted)]">
                  Paste text from any source — textbook, transcript, article, or your own notes.
                </p>
                <Textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste your lecture notes, transcript, or study material here..."
                  className="min-h-[160px] bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm resize-none"
                  data-testid="import-paste-text"
                />
                <p className="text-xs text-[var(--isp-muted)] opacity-70">
                  {pasteText.length > 0 ? `${pasteText.length} characters` : 'Min 20 characters. AI will generate summary and key points.'}
                </p>
              </div>
            )}

            {/* Batch mode toggle — only for image, audio, slides */}
            {['image', 'audio', 'slides'].includes(tab) && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid="batch-toggle">
                <input
                  type="checkbox"
                  checked={batchMode}
                  onChange={(e) => { setBatchMode(e.target.checked); setFile(null); setFiles([]); setPreview(null); }}
                  className="rounded border-[var(--isp-glass-border)] accent-violet-500"
                />
                <span className="text-xs text-[var(--isp-body)]">
                  Batch upload (multiple files)
                </span>
              </label>
            )}

            {/* Import button */}
            <Button
              onClick={handleImport}
              disabled={importing || !subjectId || (tab !== 'text' && !batchMode && !title.trim())}
              className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
              data-testid="import-submit-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {getImportingLabel(batchMode, tab)}
                </>
              ) : (
                <>
                  <activeTab.icon className="w-4 h-4 mr-2" />
                  {batchMode && files.length > 0 ? `Import ${files.length} Files` : `Import ${activeTab.label}`}
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
